import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  Stack,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon, Clear as ClearIcon, Print as PrintIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import {
  getMasterUnits,
  MasterUnitFilters,
  MasterUnit,
  PaginatedResponse,
  printMasterUnit,
} from '../../services/masterUnitApi';
import { useTheme } from '../../contexts/ThemeContext';
import GenericButton from '../../components/GenericButton';
import StatusBadge from '../../components/StatusBadge';
import DateRangePicker from '../../components/DateRangePicker';

const MasterUnits: React.FC = () => {
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [masterUnits, setMasterUnits] = useState<MasterUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  const [filters, setFilters] = useState<MasterUnitFilters>({
    search: '',
    status_id: '',
    journey_type: '',
    date_from: '',
    date_to: '',
    page: 1,
    per_page: 20
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchMasterUnits();
  }, [filters]);

  const fetchMasterUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<MasterUnit> = await getMasterUnits(filters);
      setMasterUnits(response.data);
      setPagination({
        current_page: response.current_page,
        per_page: response.per_page,
        total: response.total,
        last_page: response.last_page
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof MasterUnitFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      page: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status_id: '',
      journey_type: '',
      date_from: '',
      date_to: '',
      page: 1,
      per_page: 20
    });
  };

  const getStatusBadgeClass = (statusId: number) => {
    const statusClasses: { [key: number]: string } = {
      5: 'bg-yellow-100 text-yellow-800',
      6: 'bg-blue-100 text-blue-800',
      7: 'bg-green-100 text-green-800',
      15: 'bg-red-100 text-red-800'
    };
    return statusClasses[statusId] || 'bg-gray-100 text-gray-800';
  };

  const getJourneyTypeBadgeClass = (journeyType: string) => {
    const typeClasses: { [key: string]: string } = {
      'Forward': 'bg-blue-100 text-blue-800',
      'Reverse': 'bg-orange-100 text-orange-800',
      'Failed Delivery': 'bg-red-100 text-red-800'
    };
    return typeClasses[journeyType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{ color: theme.palette.text.primary, fontWeight: 600, textAlign: isMobile ? 'center' : 'left' }}
        >
          Master Units
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
          <GenericButton onClick={() => navigate('/master-units/create')}>
            Add New Master Unit
          </GenericButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters Card */}
      <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Filters
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Search by MU or Seal #"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch as any}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status_id || ''}
                  onChange={(e) => handleFilterChange('status_id', e.target.value || '')}
                  label="Status"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="5">Ready to Ship</MenuItem>
                  <MenuItem value="6">Shipped</MenuItem>
                  <MenuItem value="7">Arrived at Hub</MenuItem>
                  <MenuItem value="15">Failed Delivery</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Journey Type</InputLabel>
                <Select
                  value={filters.journey_type || ''}
                  onChange={(e) => handleFilterChange('journey_type', e.target.value || '')}
                  label="Journey Type"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Forward">Forward</MenuItem>
                  <MenuItem value="Reverse">Reverse</MenuItem>
                  <MenuItem value="Failed Delivery">Failed Delivery</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                fullWidth
                sx={{ borderRadius: 3 }}
              >
                Clear
              </Button>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box>
                <DateRangePicker
                  fromDate={filters.date_from || ''}
                  toDate={filters.date_to || ''}
                  onDateChange={(from, to) => {
                    handleFilterChange('date_from', from);
                    handleFilterChange('date_to', to);
                  }}
                  onClear={() => {
                    handleFilterChange('date_from', '');
                    handleFilterChange('date_to', '');
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Card */}
      <Card sx={{ bgcolor: theme.palette.background.paper, display: isMobile ? 'none' : 'block' }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ position: 'relative', minHeight: 400 }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.6)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Master Unit #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Journey Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Destination</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Consignments</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Handover Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterUnits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          No master units found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    masterUnits.map((masterUnit) => (
                      <TableRow key={masterUnit.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Link to={`/master-units/${masterUnit.id}`} style={{ textDecoration: 'none' }}>
                              <Typography color="primary" fontWeight={600}>
                                {masterUnit.mu_number}
                              </Typography>
                            </Link>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={masterUnit.journey_type} size="small" />
                        </TableCell>
                        <TableCell>
                          {masterUnit.journey_type === 'Failed Delivery' ? (
                            masterUnit.client?.name || 'N/A'
                          ) : (
                            <Box>
                              <Typography variant="body2">{masterUnit.destinationCity?.name || 'N/A'}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({masterUnit.inboundBranch?.name || 'N/A'})
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{masterUnit.consignments?.length || 0}</TableCell>
                        <TableCell>
                          {masterUnit.consignments?.filter((c) => c.statusInMasterUnit === 'Hand Over').length || 0}
                        </TableCell>
                        <TableCell>{new Date(masterUnit.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <StatusBadge status={{ id: masterUnit.status_id, name: masterUnit.status?.name || 'Unknown' }} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" component={Link as any} to={`/master-units/${masterUnit.id}`}>
                              <ViewIcon />
                            </IconButton>
                            {masterUnit.status_id === 5 && (
                              <>
                                <IconButton size="small" component={Link as any} to={`/master-units/${masterUnit.id}/edit`}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => { /* delete */ }}>
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                            <IconButton size="small" onClick={() => printMasterUnit(masterUnit.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={(_e, page) => handlePageChange(page)}
                color="primary"
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
              />
            </Box>
          )}

          {/* Results Summary */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to
              {' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Mobile Card Layout */}
      {isMobile && (
        <Card sx={{ bgcolor: theme.palette.background.paper, mt: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ position: 'relative', minHeight: 200 }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.6)',
                    zIndex: 1,
                    borderRadius: 3,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {masterUnits.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No master units found
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {masterUnits.map((mu) => (
                    <Card key={mu.id} sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {mu.mu_number}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <Chip label={mu.journey_type} size="small" />
                              <StatusBadge status={{ id: mu.status_id, name: mu.status?.name || 'Unknown' }} />
                            </Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                              {mu.journey_type === 'Failed Delivery' ? (mu.client?.name || 'N/A') : (mu.destinationCity?.name || 'N/A')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Consignments: {mu.consignments?.length || 0} | Handover: {mu.consignments?.filter((c) => c.statusInMasterUnit === 'Hand Over').length || 0}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" component={Link as any} to={`/master-units/${mu.id}`} sx={{ bgcolor: theme.palette.action.hover, '&:hover': { bgcolor: theme.palette.action.selected } }}>
                              <ViewIcon />
                            </IconButton>
                            {mu.status_id === 5 && (
                              <IconButton size="small" component={Link as any} to={`/master-units/${mu.id}/edit`} sx={{ bgcolor: theme.palette.action.hover, '&:hover': { bgcolor: theme.palette.action.selected } }}>
                                <EditIcon />
                              </IconButton>
                            )}
                            <IconButton size="small" sx={{ bgcolor: theme.palette.action.hover, '&:hover': { bgcolor: theme.palette.action.selected } }} onClick={() => printMasterUnit(mu.id)}>
                              <PrintIcon />
                            </IconButton>
                          </Stack>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Date
                            </Typography>
                            <Typography variant="body2">{new Date(mu.created_at).toLocaleDateString()}</Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Branch
                            </Typography>
                            <Typography variant="body2">{mu.inboundBranch?.name || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MasterUnits;
